import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { Button } from "@/components/ui/button";
import { ShellAgentIcon } from "@/components/ui/shell-agent-icon";
import { CheckCircle } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { isElectronRuntime } from "@/lib/logout";
import { queries } from "@/lib/queries";
import {
  SHELL_AGENT_OPTIONS,
  type ShellAgentId,
  readShellAgentPreference,
  setShellAgentPreference,
} from "@/lib/shell-agent";
import { markCliCredsPending } from "@/lib/cli-creds-flag";
import {
  buildConnectAgentSteps,
  buildFinaleMessage,
  type ConnectAgentStep,
  type ConnectAgentStepId,
} from "./connect-agent-steps";
import {
  detectAgentMessage,
  findGeneralChannel,
} from "./connect-agent-detection";
import { ConnectAgentStepRow } from "./connect-agent-panels";
import { RevealStagger } from "./RevealStagger";
import { StepActions, StepHeading } from "./onboarding-chrome";

// We watch a small window of recent #general messages. New workspaces have a
// near-empty #general, so this is plenty to capture the snapshot baseline and
// the agent's incoming post.
const GENERAL_WATCH_LIMIT = 20;

/** Fire-and-forget: open the shell and auto-run the command in the PTY. */
function runInShell(command: string) {
  window.dispatchEvent(new CustomEvent("terminal:open"));
  window.dispatchEvent(
    new CustomEvent("terminal:rerun", { detail: { command } }),
  );
}

interface ConnectAgentDialogProps {
  userId: string;
  /** Current workspace — used to locate #general for the finale. */
  workspaceId: string | null;
  firstName?: string;
  /** Whether the docked terminal is open (so the surface ends at its edge). */
  terminalOpen: boolean;
  /** Width of the docked terminal, in px. */
  terminalWidth: number;
  /** Reports open/close so the host can hide the floating terminal toggle. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Desktop-only "Connect your agent" overlay. Replaces the old first-launch
 * `OnboardingFlow` gauntlet: it's opened on demand from the Inbox "Set up your
 * code agent" card (`ano:open-agent-setup` event) and walks the user through
 * wiring up THEIR OWN code agent — Claude Code / Codex / Gemini.
 *
 * Layout: an opaque surface that fills the screen from the left edge up to the
 * docked terminal's left edge (no dead gap). The steps live in a left column;
 * each step's launch sends its command to the shell on the right where the
 * agent runs.
 *
 * Detection is a bonus, never a gate: CLI credential install is async (and a
 * no-op in dev:local), so a Done button always finishes the flow even if the
 * celebration never auto-fires.
 */
export function ConnectAgentDialog({
  userId,
  workspaceId,
  firstName,
  terminalOpen,
  terminalWidth,
  onOpenChange,
}: ConnectAgentDialogProps) {
  const [open, setOpen] = useState(false);
  const [agentId, setAgentId] = useState<ShellAgentId>(() =>
    readShellAgentPreference(),
  );
  const [completed, setCompleted] = useState<ReadonlySet<ConnectAgentStepId>>(
    new Set(),
  );
  // True once the finale command has been sent to the shell.
  const [watching, setWatching] = useState(false);
  // True once the finale PROMPT was actually sent ("Ask your agent") — NOT just
  // "Mark done". Lets a late #general sync arm the watch (below) without ever
  // watching when the user explicitly skipped asking the agent.
  const [finaleSent, setFinaleSent] = useState(false);
  // The #general message ids already present when the finale fired. A post
  // counts as "the agent connected" only if it's NEW relative to this snapshot
  // (id-based, so no clock-skew math and no false-celebrating on a message the
  // user typed before clicking).
  const [seenIds, setSeenIds] = useState<ReadonlySet<string>>(new Set());
  // The workspace the watch was started for. If the user navigates to another
  // workspace while the panel is open, the snapshot no longer applies — gate
  // detection on this so a stale baseline can't false-fire the banner.
  const [watchWorkspace, setWatchWorkspace] = useState<string | null>(null);

  // Opened only from the Inbox card. Guarded to Electron — the steps run shell
  // commands, which need the desktop PTY.
  useEffect(() => {
    const openHandler = () => {
      if (isElectronRuntime()) setOpen(true);
    };
    window.addEventListener("ano:open-agent-setup", openHandler);
    return () =>
      window.removeEventListener("ano:open-agent-setup", openHandler);
  }, []);

  // Open the app's shell terminal on the right as soon as the panel opens — and
  // re-open it if the user collapses it while the surface is up (otherwise the
  // surface snaps full-width with no shell to launch the agent into). The same
  // panel the steps drive via `terminal:rerun` — we reuse it, not a second one.
  useEffect(() => {
    if (open && !terminalOpen) {
      window.dispatchEvent(new CustomEvent("terminal:open"));
    }
  }, [open, terminalOpen]);

  // Report open/close so the host can hide the floating terminal-toggle button
  // while this takeover is up (it would otherwise hover over the surface).
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const [channels] = useQuery(
    open && workspaceId
      ? queries.channels.byWorkspaceAndUser(workspaceId)
      : null,
  );
  const generalId = useMemo(
    () => findGeneralChannel(channels)?.id ?? null,
    [channels],
  );

  // Subscribe to #general as soon as the dialog is open and we know the id, so
  // a baseline snapshot is available the moment the finale fires.
  const [generalMessages] = useQuery(
    open && generalId
      ? queries.messages.byChannelComposite({
          channelId: generalId,
          limit: GENERAL_WATCH_LIMIT,
        })
      : null,
  );
  // Latest messages, read inside the (stable) finale handler without making it
  // a dep. Updated in an effect (the finale click happens after commit, so the
  // ref is always current by then).
  const generalMessagesRef = useRef(generalMessages);
  useEffect(() => {
    generalMessagesRef.current = generalMessages;
  }, [generalMessages]);

  // Late-sync arming. The finale prompt can be sent before #general has synced
  // (its run button is never gated — detection is a bonus, not a gate). If that
  // happened (sent, but the watch never armed because the baseline was
  // undefined), arm it the moment a baseline first appears, so a late sync +
  // agent post STILL celebrates. Done as a convergent set DURING render (React's
  // "adjust state when data changes" pattern) rather than an effect — the
  // `!watching` guard self-disables it after one arm, and using the first
  // defined baseline as the snapshot keeps a pre-existing post from false-firing.
  if (
    finaleSent &&
    !watching &&
    generalMessages !== undefined &&
    workspaceId != null
  ) {
    setSeenIds(new Set(generalMessages.map((m) => m.id)));
    setWatchWorkspace(workspaceId);
    setWatching(true);
  }

  const detected = useMemo(
    () =>
      watching && watchWorkspace === workspaceId
        ? detectAgentMessage(generalMessages, seenIds, userId)
        : null,
    [watching, watchWorkspace, workspaceId, generalMessages, seenIds, userId],
  );

  const agentOption =
    SHELL_AGENT_OPTIONS.find((o) => o.value === agentId) ??
    SHELL_AGENT_OPTIONS[0];
  const finaleMessage = useMemo(
    () => buildFinaleMessage(firstName),
    [firstName],
  );
  const steps = useMemo(
    () =>
      buildConnectAgentSteps({
        agentId,
        agentLabel: agentOption.label,
        agentCommand: agentOption.command,
        finaleMessage,
      }),
    [agentId, agentOption, finaleMessage],
  );

  // A detected post implies the finale succeeded — treat it as completed
  // without mirroring detection into state (which would cascade a render).
  const isStepCompleted = useCallback(
    (id: ConnectAgentStepId) =>
      completed.has(id) || (id === "finale" && detected != null),
    [completed, detected],
  );

  const activeIndex = useMemo(() => {
    const idx = steps.findIndex((s) => !isStepCompleted(s.id));
    return idx === -1 ? steps.length : idx;
  }, [steps, isStepCompleted]);

  const markStepSideEffects = useCallback((stepId: ConnectAgentStepId) => {
    // Installing the CLI flags creds for install on the next workspace entry,
    // whether the user ran the command or just marked the step done.
    if (stepId === "cli") markCliCredsPending();
  }, []);

  const handleRun = useCallback(
    (step: ConnectAgentStep) => {
      runInShell(step.command);
      markStepSideEffects(step.id);
      if (step.id === "finale") {
        // Detection is a bonus, never a gate: the prompt ALWAYS sends to the
        // shell (like every other step). Mark it sent so a late #general sync
        // can arm the watch (effect above). If a baseline is already loaded,
        // arm immediately here so the snapshot excludes the not-yet-posted
        // message; an empty/undefined baseline must never false-fire.
        setFinaleSent(true);
        const baseline = generalMessagesRef.current;
        if (baseline !== undefined) {
          setSeenIds(new Set(baseline.map((m) => m.id)));
          setWatchWorkspace(workspaceId);
          setWatching(true);
        }
      }
      setCompleted((prev) => new Set(prev).add(step.id));
    },
    [markStepSideEffects, workspaceId],
  );

  const handleComplete = useCallback(
    (stepId: ConnectAgentStepId) => {
      markStepSideEffects(stepId);
      setCompleted((prev) => new Set(prev).add(stepId));
    },
    [markStepSideEffects],
  );

  const handleAgentChange = useCallback((next: ShellAgentId) => {
    setShellAgentPreference(next);
    setAgentId(next);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  if (!open) return null;

  // Opaque surface from the left edge to the terminal's left edge. The steps
  // sit in the left column.
  return (
    <div
      // z-50 so the surface paints over the floating terminal-toggle button
      // (also z-50, but earlier in AppDialogs' DOM) — otherwise that button
      // hovers over the blank canvas whenever the terminal is closed.
      className="fixed inset-y-0 left-0 z-50 flex flex-col bg-background animate-in fade-in duration-200"
      style={{ right: terminalOpen ? terminalWidth : 0 }}
    >
      {/* Window-drag strip — clears the macOS traffic lights (hiddenInset). */}
      <div className="h-[28px] shrink-0" data-tauri-drag-region />

      <div className="flex min-h-0 flex-1">
        {/* Left column: the steps — vertically centered (like the onboarding
            wizard), with the actions pinned to the bottom. Generous left
            padding (matches OnboardingScreen's px-6/sm:px-10/lg:px-16) so the
            content breathes off the screen edge. */}
        <div className="flex w-full max-w-2xl shrink-0 flex-col">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-10 sm:px-10 lg:px-16">
            <div className="my-auto w-full space-y-7">
              <div className="reveal-item" style={{ animationDelay: "40ms" }}>
                <StepHeading
                  title="Connect your code agent"
                  subtitle="Wire up your own agent — Claude Code, Codex, or Gemini. It launches in the shell on the right; follow the steps here."
                />
              </div>

              <div
                className="reveal-item flex gap-1.5"
                style={{ animationDelay: "190ms" }}
              >
                {SHELL_AGENT_OPTIONS.map((o) => (
                  <Button
                    key={o.value}
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleAgentChange(o.value)}
                    aria-pressed={o.value === agentId}
                    className={cn(
                      "flex-1 justify-start gap-2 shadow-none",
                      o.value === agentId
                        ? "border-foreground/30 bg-foreground/10 text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    <ShellAgentIcon agentId={o.value} className="size-4" />
                    {o.label}
                  </Button>
                ))}
              </div>

              <div className="space-y-2.5">
                <RevealStagger base={270} step={75}>
                  {steps.map((step, i) => (
                    <ConnectAgentStepRow
                      key={step.id}
                      step={step}
                      index={i}
                      isActive={i === activeIndex}
                      isCompleted={isStepCompleted(step.id)}
                      // Every step — finale included — can always send to the
                      // shell. Detection readiness (#general loaded) gates only
                      // the celebration, handled in handleRun, never the send.
                      runDisabled={false}
                      onRun={() => handleRun(step)}
                      onComplete={() => handleComplete(step.id)}
                    />
                  ))}
                </RevealStagger>
              </div>

              {detected && (
                <div className="flex items-center gap-2 rounded-md bg-foreground/[0.04] px-3 py-2.5 text-sm text-foreground">
                  <CheckCircle
                    size={15}
                    weight="Bold"
                    className="shrink-0 text-foreground/70"
                  />
                  {agentOption.label} just posted to #general — you&rsquo;re
                  connected.
                </div>
              )}
            </div>
          </div>

          <div
            className="reveal-item shrink-0 px-6 py-6 sm:px-10 lg:px-16"
            style={{ animationDelay: "560ms" }}
          >
            {/* No top × — "Later" dismisses, "Finish setup" completes. */}
            <StepActions
              onSkip={handleClose}
              skipLabel="Later"
              primaryLabel={detected ? "Done" : "Finish setup"}
              onPrimary={handleClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
